import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  forwardRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import mapSchema from '@galo-monitor/forms/schemas/farm/map.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PropertyActions } from '@galo-monitor/stores/property'
import { selectCurrentProperty, selectUnitOfMeasurement } from '@galo-monitor/stores/property/selectorProperty'
import colors from '@galo-monitor/styles/colors'
import { getPolygonCenter } from '@galo-monitor/utils/maps'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import GoogleMaps from '@galo-monitor/web-components/Maps/Google'
import DrawingManager from '@galo-monitor/web-components/Maps/Google/components/DrawingManager'
import PopupPolygon from '@galo-monitor/web-components/Maps/Google/components/PopupPolygon'
import withGoogleMaps from '@galo-monitor/web-components/Maps/Google/hoc/withGoogleMaps'

const MapForm = withGoogleMaps(props => {
  const {
    mapObject,
    loading,
    setLoading,
    setBlockForm,
    onSuccess,
    onError,
    formRef,
    isGoogleMapsLoaded
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()
  const currentProperty = useSelector(selectCurrentProperty)
  const { initials: unitInitials } = useSelector(selectUnitOfMeasurement)

  const { data: mapData, error: mapError, isValidating } = useGaloMonitorApi(mapObject.id ? `/farms/properties/maps/${ mapObject.id }` : null)
  const { data: mapTypes } = useGaloMonitorApi('/farms/properties/maps/types')
  const { data: existingPlots } = useGaloMonitorApi('/farms/plots/properties')

  const mounted = useRef(true)
  const polygonRef = useRef(null)

  const mapTypeOptions = useMemo(
    () => map(mapTypes, mapType => ({
      label: mapType.name,
      value: mapType.id.toString()
    })),
    [mapTypes]
  )

  const polygonData = useMemo(() => {
    if (!isEmpty(mapData?.coordinates)) {
      return map(
        mapData.coordinates,
        point => ({ lat: +point.lat, lng: +point.long })
      )
    }

    if (!isEmpty(map?.coordinates)) {
      return map(
        map.coordinates,
        point => ({ lat: +point.lat, lng: +point.long })
      )
    }

    return []
  }, [mapData])

  const existingPlotsData = useMemo(() => {
    if (!isEmpty(existingPlots)) {
      return map(
        existingPlots,
        ({ plot }) => {
          if (!isEmpty(plot.coordinates))
            return {
              id: plot.id,
              name: plot.name,
              paths: map(plot.coordinates, point => ({
                lat: +point.lat,
                lng: +point.long
              }))
            }
          return null
        }
      )
    }

    return []
  }, [existingPlots])

  const mapCenter = useMemo(
    () => {
      if (!isEmpty(mapData?.coordinates)) {
        return getPolygonCenter(
          map(mapData.coordinates, path => ({
            latitude: +path.lat,
            longitude: +path.long
          }))
        )
      }

      if (!isEmpty(mapObject?.coordinates)) {
        return getPolygonCenter(
          map(mapObject.coordinates, path => ({
            latitude: +path.lat,
            longitude: +path.long
          }))
        )
      }

      return {
        lat: +currentProperty.lat,
        lng: +currentProperty.long
      }
    },
    [currentProperty.lat, currentProperty.long, mapObject, mapData]
  )

  const onToggleLockMap = useCallback(() => setBlockForm(prevState => !prevState), [setBlockForm])

  const handleSubmit = useCallback(
    data => {
      setLoading(true)
      const coordinates = map(
        polygonRef.current.props.paths,
        polygon => ({
          lat: polygon.lat,
          long: polygon.lng
        })
      )

      const { area } = polygonRef.current.props

      dispatch(PropertyActions.savePropertyMap(
        {
          ...mapObject,
          ...data,
          area: area[unitInitials],
          coordinates
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('map', { howMany: 1 }),
            action: toLower(t(isEmpty(mapObject) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [setLoading, dispatch, mapObject, unitInitials, onError, snackbar, t, onSuccess]
  )

  useEffect(() => () => {
    mounted.current = false
  }, [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ mapSchema }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 3 }>
          <InputText
            name="name"
            label={ t('name of {this}', { this: t('map', { howMany: 1 }), gender: 'male', howMany: 1 }) }
            defaultValue={ mapObject.name }
            disabled={ loading }
            fullWidth
          />

          <InputSelect
            name="property_map_classification_id"
            label={ t('map classification') }
            options={ mapTypeOptions }
            defaultValue={ mapObject.propertyMapClassificationId?.toString() ?? '' }
            disabled={ loading }
            loading={ isValidating }
            fullWidth
          />

          <InputText
            name="observation"
            label={ t('observation', { howMany: 2 }) }
            disabled={ loading }
            defaultValue={ mapObject.observation ?? '' }
            fullWidth
          />
        </Grid>

        <Grid item xs={ 9 }>
          <div style={ { flex: 1, display: 'flex', flexDirection: 'column' } }>
            <div style={ { height: '60vh' } }>
              <GoogleMaps
                isLoaded={ isGoogleMapsLoaded }
                mapContainerStyle={ {
                  width: '100%',
                  height: '60vh'
                } }
                center={ mapCenter }
                zoom={ 12 }
              >
                <DrawingManager
                  ref={ polygonRef }
                  isLoadingOverlayData={ (!mapData && !mapError) || isValidating }
                  onToggleLockMap={ onToggleLockMap }
                  overlayData={ polygonData }
                  convertUnit={ unitInitials }
                />
                <>
                  {
                    map(existingPlotsData, existingPlot => (
                      <PopupPolygon
                        key={ existingPlot.id }
                        paths={ existingPlot.paths }
                        options={ {
                          fillOpacity: 0.2,
                          strokeOpacity: 0.8,
                          strokeWeight: 2,
                          fillColor: colors.redDark,
                          strokeColor: colors.redDark
                        } }
                      >
                        <div>{existingPlot.name}</div>
                      </PopupPolygon>
                    ))
                  }
                </>
              </GoogleMaps>
            </div>
          </div>
        </Grid>
      </Grid>
    </Form>
  )
})

MapForm.propTypes = {
  mapObject: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setBlockForm: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

MapForm.defaultProps = {
  mapObject: {},
  loading: false,
  setLoading: () => {},
  setBlockForm: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default forwardRef((props, formRef) => <MapForm formRef={ formRef } { ...props } />)
